/**
 * Call Break bot.
 *
 * - Bidding: expected-trick estimator (honours, spade length, ruffing potential) with difficulty noise.
 * - Play (BEGINNER/EASY): heuristic policy with partial card memory.
 * - Play (NORMAL+): heuristic + void tracking; HARD/EXPERT add Monte-Carlo determinised roll-outs that
 *   sample opponents' hands consistent with public information (counts + revealed voids).
 * The bot only ever reads its own seat's view.
 */
import { buildDeck, parseCard, type CardId, type Suit, SUITS } from '../../core/cards.js';
import { shuffle } from '../../core/rng.js';
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import {
  legalCards,
  scoreRound,
  trickWinner,
  type CallBreakAction,
  type CallBreakConfig,
  type CallBreakLegal,
  type CallBreakView,
  type TrickPlay,
} from './engine.js';

const TRUMP: Suit = 'S';

interface Knowledge {
  unseen: Set<CardId>;
  /** voids[seat] = suits that seat is known to lack. */
  voids: Set<Suit>[];
}

export function estimateTricks(hand: readonly CardId[]): number {
  const cards = hand.map(parseCard);
  const bySuit = new Map<Suit, number[]>();
  for (const s of SUITS) bySuit.set(s, []);
  for (const c of cards) bySuit.get(c.suit)!.push(c.rank);
  for (const ranks of bySuit.values()) ranks.sort((a, b) => b - a);
  const spades = bySuit.get(TRUMP)!;
  let est = 0;
  for (const [suit, ranks] of bySuit) {
    if (suit === TRUMP) continue;
    const len = ranks.length;
    if (ranks.includes(14)) est += len >= 7 ? 0.7 : 1;
    if (ranks.includes(13)) est += len >= 2 && len <= 4 ? (ranks.includes(14) ? 0.8 : 0.55) : len >= 5 ? 0.2 : 0.1;
    if (ranks.includes(12) && ranks.includes(13) && ranks.includes(14) && len <= 4) est += 0.4;
  }
  const sLen = spades.length;
  for (const r of spades) {
    if (r === 14) est += 1;
    else if (r === 13) est += sLen >= 2 ? 0.95 : 0.6;
    else if (r === 12) est += sLen >= 3 ? 0.75 : 0.3;
    else if (r === 11) est += sLen >= 4 ? 0.5 : 0.15;
  }
  if (sLen > 4) est += (sLen - 4) * 0.85;
  // Ruffing potential: short side suits with spare low spades.
  const spareSpades = Math.max(0, sLen - spades.filter((r) => r >= 12).length);
  let ruffs = 0;
  for (const [suit, ranks] of bySuit) {
    if (suit === TRUMP) continue;
    if (ranks.length === 0) ruffs += 1.0;
    else if (ranks.length === 1) ruffs += 0.5;
  }
  est += Math.min(ruffs, spareSpades) * 0.7;
  return est;
}

function buildKnowledge(view: CallBreakView, ctx: BotContext): Knowledge {
  const seen = new Set<CardId>(view.hand);
  const voids: Set<Suit>[] = [0, 1, 2, 3].map(() => new Set<Suit>());
  const allTricks: TrickPlay[][] = [...view.tricks.map((t) => t.plays)];
  if (view.phase === 'PLAYING' && view.trick.length > 0) allTricks.push(view.trick);
  const remembered = Math.round(allTricks.length * ctx.profile.memory);
  allTricks.forEach((plays, idx) => {
    const remember = idx >= allTricks.length - Math.max(remembered, 1) || ctx.profile.memory >= 1;
    const lead = parseCard(plays[0]!.card).suit;
    for (const p of plays) {
      if (remember) seen.add(p.card);
      const c = parseCard(p.card);
      if (remember && ctx.profile.memory > 0.5 && c.suit !== lead) {
        voids[p.seat]!.add(lead);
        if (c.suit !== TRUMP && view.config.mustTrumpIfVoid) voids[p.seat]!.add(TRUMP);
      }
    }
  });
  // Even forgetful bots never think a card in the current trick is unseen.
  view.trick.forEach((p) => seen.add(p.card));
  const unseen = new Set(buildDeck().filter((c) => !seen.has(c)));
  return { unseen, voids };
}

const power = (id: CardId) => {
  const c = parseCard(id);
  return (c.suit === TRUMP ? 100 : 0) + c.rank;
};
const lowest = (cards: CardId[]) => [...cards].sort((a, b) => power(a) - power(b))[0]!;
const highest = (cards: CardId[]) => [...cards].sort((a, b) => power(b) - power(a))[0]!;

function isMaster(card: CardId, k: Knowledge): boolean {
  const c = parseCard(card);
  for (const u of k.unseen) {
    const x = parseCard(u);
    if (x.suit === c.suit && x.rank > c.rank) return false;
  }
  return true;
}

function wouldWin(trick: TrickPlay[], seat: number, card: CardId): boolean {
  if (trick.length === 0) return true;
  return trickWinner([...trick, { seat, card }]) === seat;
}

/** Fast, deterministic policy used for roll-outs and as the base heuristic. */
function heuristicCard(
  hand: CardId[],
  trick: TrickPlay[],
  seat: number,
  need: number,
  cfg: CallBreakConfig,
  k: Knowledge | null,
): CardId {
  const legal = legalCards(hand, trick, cfg);
  if (legal.length === 1) return legal[0]!;
  const remainingAfterMe = 3 - trick.length;
  if (trick.length === 0) {
    // Leading.
    if (k) {
      const masters = legal.filter((c) => parseCard(c).suit !== TRUMP && isMaster(c, k));
      const safeMasters = masters.filter((c) => {
        const s = parseCard(c).suit;
        return ![0, 1, 2, 3].some((p) => p !== seat && k.voids[p]!.has(s) && !k.voids[p]!.has(TRUMP));
      });
      if (safeMasters.length) return highest(safeMasters);
      const spades = legal.filter((c) => parseCard(c).suit === TRUMP);
      const spadeMasters = spades.filter((c) => isMaster(c, k));
      if (spadeMasters.length && spades.length >= 3) return highest(spadeMasters);
    } else {
      const aces = legal.filter((c) => parseCard(c).rank === 14 && parseCard(c).suit !== TRUMP);
      if (aces.length) return aces[0]!;
    }
    // Lead low from the shortest non-trump suit to create a void for ruffing.
    const side = legal.filter((c) => parseCard(c).suit !== TRUMP);
    const pool = side.length ? side : legal;
    const counts = new Map<Suit, number>();
    pool.forEach((c) => counts.set(parseCard(c).suit, (counts.get(parseCard(c).suit) ?? 0) + 1));
    const shortSuit = [...counts.entries()].sort((a, b) => a[1] - b[1])[0]![0];
    return lowest(pool.filter((c) => parseCard(c).suit === shortSuit));
  }
  const winners = legal.filter((c) => wouldWin(trick, seat, c));
  if (winners.length === 0) return dumpCard(legal);
  if (remainingAfterMe === 0) return lowest(winners);
  if (need > 0 || chance0(need)) {
    const lead = parseCard(trick[0]!.card).suit;
    // A winner holds if it's the master of its suit and nobody after us can ruff.
    const safe = winners.filter((c) => {
      const card = parseCard(c);
      if (!k) return card.rank >= 13 || card.suit === TRUMP;
      if (card.suit === TRUMP) return isMaster(c, k) || lead !== TRUMP;
      const masterish = isMaster(c, k);
      const laterSeats = [1, 2, 3].slice(0, remainingAfterMe).map((d) => (seat + d) % 4);
      const ruffRisk = laterSeats.some((p) => k.voids[p]!.has(lead) && !k.voids[p]!.has(TRUMP));
      return masterish && !ruffRisk;
    });
    if (safe.length) return lowest(safe);
    const trumpWinners = winners.filter((c) => parseCard(c).suit === TRUMP && parseCard(trick[0]!.card).suit !== TRUMP);
    if (trumpWinners.length) return lowest(trumpWinners);
  }
  return dumpCard(legal);
}

function chance0(need: number): boolean {
  return need === 0; // overtricks still score +0.1, take cheap ones
}

function dumpCard(legal: CardId[]): CardId {
  const side = legal.filter((c) => parseCard(c).suit !== TRUMP);
  return lowest(side.length ? side : legal);
}

// ---------------------------------------------------------------------------------------------
// Fast integer simulator for Monte-Carlo roll-outs. Card int = suitIndex * 13 + (rank - 2),
// suitIndex 0 = spades (trump). ~10x faster than the string-based engine helpers.
// ---------------------------------------------------------------------------------------------
const SUIT_INDEX: Record<Suit, number> = { S: 0, H: 1, D: 2, C: 3 };
const toInt = (id: CardId) => {
  const c = parseCard(id);
  return SUIT_INDEX[c.suit] * 13 + (c.rank - 2);
};
const suitI = (c: number) => (c / 13) | 0;
const rankI = (c: number) => c % 13;

interface FastTrick { seats: number[]; cards: number[] }

function fastWinner(t: FastTrick): number {
  let best = 0;
  for (let i = 1; i < t.cards.length; i++) {
    const c = t.cards[i]!;
    const b = t.cards[best]!;
    if ((suitI(c) === 0 && suitI(b) !== 0) || (suitI(c) === suitI(b) && c > b)) best = i;
  }
  return t.seats[best]!;
}

function fastLegal(hand: number[], t: FastTrick, cfg: CallBreakConfig, out: number[]): number[] {
  out.length = 0;
  if (t.cards.length === 0) {
    for (const c of hand) out.push(c);
    return out;
  }
  const lead = suitI(t.cards[0]!);
  let bestLead = -1;
  let bestTrump = -1;
  for (const c of t.cards) {
    if (suitI(c) === lead && c > bestLead) bestLead = c;
    if (suitI(c) === 0 && c > bestTrump) bestTrump = c;
  }
  const trumped = lead !== 0 && bestTrump >= 0;
  let hasLead = false;
  let hasHigher = false;
  let hasTrump = false;
  let hasHigherTrump = false;
  for (const c of hand) {
    const s = suitI(c);
    if (s === lead) {
      hasLead = true;
      if (c > bestLead) hasHigher = true;
    }
    if (s === 0) {
      hasTrump = true;
      if (c > bestTrump) hasHigherTrump = true;
    }
  }
  if (hasLead) {
    const mustBeat = cfg.mustBeatIfPossible && !trumped && hasHigher;
    for (const c of hand) if (suitI(c) === lead && (!mustBeat || c > bestLead)) out.push(c);
    return out;
  }
  if (cfg.mustTrumpIfVoid && hasTrump) {
    if (!trumped) {
      for (const c of hand) if (suitI(c) === 0) out.push(c);
      return out;
    }
    if (cfg.mustOvertrumpIfPossible) {
      if (hasHigherTrump) for (const c of hand) if (suitI(c) === 0 && c > bestTrump) out.push(c);
      else for (const c of hand) out.push(c);
      return out;
    }
    for (const c of hand) if (suitI(c) === 0) out.push(c);
    return out;
  }
  for (const c of hand) out.push(c);
  return out;
}

const fastPower = (c: number) => (suitI(c) === 0 ? 100 : 0) + rankI(c);

function fastPolicy(hand: number[], t: FastTrick, seat: number, need: number, cfg: CallBreakConfig, buf: number[]): number {
  const legal = fastLegal(hand, t, cfg, buf);
  if (legal.length === 1) return legal[0]!;
  if (t.cards.length === 0) {
    let ace = -1;
    let low = -1;
    for (const c of legal) {
      if (suitI(c) !== 0 && rankI(c) === 12) ace = c;
      if (suitI(c) !== 0 && (low < 0 || rankI(c) < rankI(low))) low = c;
    }
    if (ace >= 0) return ace;
    if (low >= 0) return low;
    return legal.reduce((a, b) => (fastPower(b) < fastPower(a) ? b : a));
  }
  const last = t.cards.length === 3;
  let lowestWinner = -1;
  let strongWinner = -1;
  let dump = -1;
  for (const c of legal) {
    t.cards.push(c);
    t.seats.push(seat);
    const wins = fastWinner(t) === seat;
    t.cards.pop();
    t.seats.pop();
    if (wins) {
      if (lowestWinner < 0 || fastPower(c) < fastPower(lowestWinner)) lowestWinner = c;
      if ((rankI(c) >= 11 || suitI(c) === 0) && (strongWinner < 0 || fastPower(c) < fastPower(strongWinner))) strongWinner = c;
    }
    const dumpScore = (suitI(c) === 0 ? 50 : 0) + rankI(c);
    if (dump < 0 || dumpScore < (suitI(dump) === 0 ? 50 : 0) + rankI(dump)) dump = c;
  }
  if (lowestWinner >= 0 && (last || need >= 0)) {
    if (last) return lowestWinner;
    if (strongWinner >= 0) return strongWinner;
  }
  return dump;
}

function sampleWorld(view: CallBreakView, k: Knowledge, ctx: BotContext, unseenInts: number[], voidMask: number[]): number[][] | null {
  const me = view.seat!;
  const others = [0, 1, 2, 3].filter((s) => s !== me);
  const counts = view.handCounts;
  const pool = unseenInts.slice();
  for (let attempt = 0; attempt < 12; attempt++) {
    shuffle(pool, ctx.rng);
    const hands: number[][] = [[], [], [], []];
    let ok = true;
    for (const card of pool) {
      const s = suitI(card);
      let n = 0;
      const cand: number[] = [];
      for (const p of others) {
        if (hands[p]!.length < counts[p]! && (attempt > 8 || !(voidMask[p]! & (1 << s)))) cand[n++] = p;
      }
      if (n === 0) {
        ok = false;
        break;
      }
      hands[cand[ctx.rng.int(n)]!]!.push(card);
    }
    if (ok) return hands;
  }
  void k;
  return null;
}

function monteCarloCard(view: CallBreakView, legal: CardId[], k: Knowledge, ctx: BotContext): CardId {
  const me = view.seat!;
  const cfg = view.config;
  const bids = view.bids.map((b) => b ?? 0);
  const unseenInts = [...k.unseen].map(toInt);
  const voidMask = k.voids.map((set) => [...set].reduce((m, s) => m | (1 << SUIT_INDEX[s]), 0));
  const myHand = view.hand.map(toInt);
  const baseTrick: FastTrick = { seats: view.trick.map((p) => p.seat), cards: view.trick.map((p) => toInt(p.card)) };
  const perCard = Math.max(6, Math.floor(ctx.profile.simulations / legal.length));
  const buf: number[] = [];
  let best = legal[0]!;
  let bestScore = -Infinity;
  for (const cardId of legal) {
    const card = toInt(cardId);
    let total = 0;
    let n = 0;
    for (let i = 0; i < perCard; i++) {
      const hands = sampleWorld(view, k, ctx, unseenInts, voidMask);
      if (!hands) break;
      hands[me] = myHand.filter((c) => c !== card);
      const won = view.tricksWon.slice();
      const t: FastTrick = { seats: [...baseTrick.seats, me], cards: [...baseTrick.cards, card] };
      let turn = (me + 1) % 4;
      for (;;) {
        if (t.cards.length === 4) {
          const w = fastWinner(t);
          won[w]!++;
          t.cards.length = 0;
          t.seats.length = 0;
          turn = w;
          if (hands[w]!.length === 0) break;
        }
        const h = hands[turn]!;
        if (h.length === 0) break;
        const c = fastPolicy(h, t, turn, bids[turn]! - won[turn]!, cfg, buf);
        h.splice(h.indexOf(c), 1);
        t.cards.push(c);
        t.seats.push(turn);
        turn = (turn + 1) % 4;
      }
      const mine = scoreRound(bids[me]!, won[me]!, cfg);
      let opp = 0;
      for (let s = 0; s < 4; s++) if (s !== me && won[s]! >= bids[s]!) opp++;
      total += mine - opp * 2;
      n++;
    }
    const avg = n ? total / n : -Infinity;
    if (avg > bestScore) {
      bestScore = avg;
      best = cardId;
    }
  }
  return best;
}

export const callBreakBot: BotEngine<CallBreakView, CallBreakLegal, CallBreakAction> = {
  decideAction(view, legal, ctx) {
    if (legal.kind === 'bid') return this.chooseBet!(view, legal, ctx);
    if (legal.kind === 'play') return this.chooseCard!(view, legal, ctx);
    throw new Error('Bot asked to act without legal actions');
  },

  chooseBet(view, legal, ctx) {
    if (legal.kind !== 'bid') return this.handleTimeout(view, legal, ctx);
    let est = estimateTricks(view.hand);
    if (ctx.difficulty === 'BEGINNER') est += (ctx.rng.next() - 0.5) * 2.5;
    else if (ctx.difficulty === 'EASY') est += (ctx.rng.next() - 0.5) * 1.5;
    // Stronger bots shade bids down slightly — failing costs the whole bid.
    const shade = ctx.difficulty === 'HARD' || ctx.difficulty === 'EXPERT' ? 0.25 : 0.1;
    const value = Math.min(legal.max, Math.max(legal.min, Math.round(est - shade)));
    return { type: 'bid', value };
  },

  chooseCard(view, legal, ctx) {
    if (legal.kind !== 'play') return this.handleTimeout(view, legal, ctx);
    const cards = legal.cards;
    if (cards.length === 1) return { type: 'play', card: cards[0]! };
    if (chance(ctx, ctx.profile.mistakeRate)) return { type: 'play', card: cards[ctx.rng.int(cards.length)]! };
    const k = buildKnowledge(view, ctx);
    const me = view.seat!;
    if (ctx.profile.simulations >= 100 && view.hand.length <= 12) {
      return { type: 'play', card: monteCarloCard(view, cards, k, ctx) };
    }
    const need = (view.bids[me] ?? 0) - view.tricksWon[me]!;
    const card = heuristicCard(view.hand, view.trick, me, need, view.config, ctx.profile.memory > 0 ? k : null);
    return { type: 'play', card: cards.includes(card) ? card : cards[0]! };
  },

  evaluateState(view) {
    const me = view.seat;
    if (me === null) return 0;
    const bid = view.bids[me] ?? Math.round(estimateTricks(view.hand));
    const projected = view.tricksWon[me]! + estimateTricks(view.hand) * (view.hand.length / 13);
    return Math.max(0, Math.min(1, 0.5 + (projected - bid) / 4));
  },

  calculateRisk(view, legal) {
    if (legal.kind === 'bid') return Math.min(1, Math.max(0, (legal.max - estimateTricks(view.hand)) / 13));
    return 0;
  },

  handleTimeout(view, legal) {
    if (legal.kind === 'bid') return { type: 'bid', value: Math.max(legal.min, Math.min(legal.max, Math.round(estimateTricks(view.hand)))) };
    if (legal.kind === 'play') return { type: 'play', card: lowest(legal.cards) };
    throw new Error('No legal action');
  },
};
